import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { pauseScroll, resumeScroll } from '@/lib/smoothScroll';

const menuItems = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/technicians', label: 'Technicians' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/about', label: 'About' },
  { to: '/service-areas', label: 'Service Areas' },
  { to: '/book', label: 'Book a Repair' },
];

interface HeaderProps {
  overlay?: boolean;
}

export default function Header({ overlay = false }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const { body, documentElement } = document;
    const scrollbar = window.innerWidth - documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    pauseScroll();
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
      resumeScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const light = overlay && !scrolled && !open;
  const pill = 'border-foreground-950/10 bg-white/95 text-[#222222] hover:bg-white';

  let headerBg = 'border-b border-background-200/70 bg-background-50/95 backdrop-blur';
  if (open) headerBg = 'bg-white';
  else if (light) headerBg = 'bg-transparent';

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-[60] transition-colors duration-300 ${headerBg}`}>
        <div className="grid h-[72px] md:h-[88px] grid-cols-3 items-center px-5 md:px-10">
          <div className="flex items-center">
            <button
              type="button"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="site-menu"
              onClick={() => setOpen(!open)}
              className={`flex h-10 w-10 md:h-11 md:w-11 cursor-pointer items-center justify-center rounded-full border transition-colors ${pill}`}
            >
              <i className={`${open ? 'ri-close-line' : 'ri-menu-4-line'} text-lg`} />
            </button>
          </div>

          <Link to="/" className={`justify-self-center text-sm md:text-[15px] font-semibold tracking-[0.32em] ${light ? 'text-white' : 'text-foreground-950'}`}>
            MENDWELL
          </Link>

          <div className="flex justify-end">
            <Link
              to="/book"
              className={`whitespace-nowrap rounded-full px-4 md:px-5 h-10 md:h-11 inline-flex items-center text-[13px] md:text-sm transition-colors ${light ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
            >
              Book a Repair
            </Link>
          </div>
        </div>
      </header>

      <div
        id="site-menu"
        aria-hidden={!open}
        data-lenis-prevent
        className={`fixed inset-0 z-[55] overflow-y-auto bg-white transition-opacity duration-300 ${open ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
      >
        <nav className="mx-auto max-w-[1320px] px-5 md:px-10 pt-[104px] md:pt-[128px] pb-12">
          {menuItems.map((item, i) => (
            <Link
              key={item.to}
              to={item.to}
              tabIndex={open ? 0 : -1}
              onClick={() => setOpen(false)}
              className="group flex items-baseline gap-5 border-b border-background-200 py-5 text-foreground-950"
            >
              <span className="text-xs text-foreground-600">{`0${i + 1}`}</span>
              <span className="text-2xl md:text-4xl font-light tracking-tight group-hover:text-primary-600 transition-colors">{item.label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </>
  );
}